import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Check, FilePlus2, Plus, SlidersHorizontal, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import {
  JOB_LIST_SCOPES,
  JOB_OPENING_STATUSES,
  JOB_STATUS_LABELS,
  type JobBuilderSection,
  type JobListFacets,
  type JobListRow,
  type JobListScope,
  type JobOpeningStatus,
  type StageBarKey,
} from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Checkbox } from '../../../components/Checkbox';
import { Chip } from '../../../components/Chip';
import { ColumnsMenuButton } from '../../../components/data-table/ColumnsMenu';
import { DataTable } from '../../../components/data-table/DataTable';
import type { TableViewsConfig } from '../../../components/data-table/TableViews';
import type { Column, SortSpec } from '../../../components/data-table/types';
import { useTablePreferences } from '../../../components/data-table/use-table-preferences';
import { Field } from '../../../components/Field';
import { SearchInput } from '../../../components/Input';
import { Pager } from '../../../components/Pager';
import { pageCountOf } from '../../../components/pager-items';
import { MenuGroup, MenuItem, MenuSeparator, Popover, usePopover } from '../../../components/Popover';
import { Segmented } from '../../../components/Segmented';
import { Select } from '../../../components/Select';
import { JOB_STATUS_TONES } from '../../../components/status-tones';
import { EmptyState, FilteredEmptyState, ForbiddenState } from '../../../components/system-states/SystemState';
import { ToneChip, toneClass } from '../../../components/Tone';
import { EMPTY, formatDate, formatDateTimeLong, formatIsoDate, formatNumber, formatRelative } from '../../../lib/format';
import { useModule } from '../../../lib/modules';
import { EMPTY_SELECTION, type SelectionState } from '../../../lib/selection';
import { useCanEdit, useCanView } from '../../../stores/session';
import { useUi, type Density } from '../../../stores/ui';
import { useDepartments, useVentures } from '../../organisation/api/organisation-api';
import { useJobListFacets, type JobListFacetParams } from '../api/job-list-api';
import { useJobs, useReadyRequisitions } from '../api/jobs-api';
import { StageTrackCell } from '../components/StageTrack';
import { useBuilderSections } from '../hooks/use-builder-sections';
import { readJobListSearch, viewFiltersOf, type JobListSearch } from './job-list-address';
import { jobRowHref, jobRowSegment, shownStageBar } from './job-list-link';
import styles from './JobListPage.module.css';

/**
 * JOB-01, "My jobs" (R2; Redesign Group 2, mockup Jobs.dc.html) — the jobs the viewer recruits
 * for, or every job they may see, each with a progress track of its stages (§L33) and its last activity.
 * Filters, scope and page live in the URL (06-qol §6), the View menu counts each option, and
 * "Start a job" lists the approved requisitions still waiting for theirs.
 */

const SCREEN_ID = 'job-list';
const PAGE_SIZE = 50;
/** Wide enough for a job title and its reference on one line. */
const START_MENU_WIDTH = 360;
/** The View menu's width in the mockup. */
const VIEW_MENU_WIDTH = 320;
/** The list asks the server once a search has this many letters. */
const MIN_QUERY = 2;

const SCOPE_LABELS: Readonly<Record<JobListScope, string>> = { mine: 'My jobs', all: 'All jobs' };
const DENSITY_LABELS: Readonly<Record<Density, string>> = { comfortable: 'Comfortable', compact: 'Compact' };
const DENSITIES: readonly Density[] = ['comfortable', 'compact'];
const NO_HIDDEN_STAGES: ReadonlySet<StageBarKey> = new Set();
/** As the job page: ⑦ Assessment leaves the bar while Assessments is off (§L34, #13). */
const ASSESSMENT_OFF: ReadonlySet<StageBarKey> = new Set<StageBarKey>(['assessment']);

function isJobListScope(value: string | undefined): value is JobListScope {
  return (JOB_LIST_SCOPES as readonly (string | undefined)[]).includes(value);
}

export function JobListPage(): ReactNode {
  const canView = useCanView('job_opening');
  const canCreate = useCanEdit('job_config');
  const isRequestsOn = useModule('requisitions');
  const hiddenStages = useModule('assessments') ? NO_HIDDEN_STAGES : ASSESSMENT_OFF;
  const sections = useBuilderSections();
  const navigate = useNavigate();
  const search = useSearch({ from: '/app-layout/jobs' }) as JobListSearch;
  // Q3: a recruiter starts on their own jobs, the HR Lead on all of them.
  const isHrLead = useCanView('reports_all_staff');
  const scope: JobListScope = isJobListScope(search.scope) ? search.scope : isHrLead ? 'all' : 'mine';
  const ventures = useVentures();
  const departments = useDepartments();
  const [sort, setSort] = useState<readonly SortSpec[]>([]);
  const [selection, setSelection] = useState<SelectionState>(EMPTY_SELECTION);

  const query = search.q ?? '';
  const page = search.page ?? 1;
  const statuses = (search.status?.split(',') ?? []).filter((value): value is JobOpeningStatus =>
    JOB_OPENING_STATUSES.some((status) => status === value),
  );

  const filterParams: JobListFacetParams = {
    ...(query.trim().length >= MIN_QUERY ? { query: query.trim() } : {}),
    ...(statuses.length > 0 ? { status: statuses.join(',') } : {}),
    ...(search.venture ? { ventureId: search.venture } : {}),
    ...(search.department ? { departmentId: search.department } : {}),
    scope,
  };
  const jobs = useJobs({ page, pageSize: PAGE_SIZE, ...filterParams });
  const facets = useJobListFacets(filterParams);

  const columns = jobColumns(sections, hiddenStages);
  const preferences = useTablePreferences(SCREEN_ID, columns);

  if (!canView) return <ForbiddenState resource="jobs" contact="Your admin" />;

  /** Any change of what is listed: back to page 1, and what was ticked belonged to the old list. */
  function setSearch(next: JobListSearch): void {
    setSelection(EMPTY_SELECTION);
    void navigate({ to: '/jobs', search: { ...next, page: undefined }, replace: true });
  }

  function setPage(next: number): void {
    setSelection(EMPTY_SELECTION);
    void navigate({ to: '/jobs', search: { ...search, page: next > 1 ? next : undefined }, replace: true });
  }

  const rows = jobs.data?.rows ?? [];
  const total = jobs.data?.total;
  const ventureName =
    facets.data?.ventures.find((v) => v.value === search.venture)?.label ?? ventures.data?.find((v) => v.id === search.venture)?.name;
  const departmentName = departments.data?.find((d) => d.id === search.department)?.name;
  const clearFilters = (): void => setSearch({ scope: search.scope });

  const filters = [
    statuses.length > 0
      ? {
          id: 'status',
          label: `Status: ${statuses.map((s) => JOB_STATUS_LABELS[s]).join(', ')}`,
          onRemove: () => setSearch({ ...search, status: undefined }),
        }
      : null,
    ventureName
      ? { id: 'venture', label: `Venture: ${ventureName}`, onRemove: () => setSearch({ ...search, venture: undefined, department: undefined }) }
      : null,
    departmentName
      ? { id: 'department', label: `Department: ${departmentName}`, onRemove: () => setSearch({ ...search, department: undefined }) }
      : null,
  ].filter((filter) => filter !== null);
  const filterCount = filters.length + (query === '' ? 0 : 1);

  const views: TableViewsConfig = { screen: 'jobs', filters: viewFiltersOf(search), onApply: (next) => setSearch(readJobListSearch(next)) };
  const openJob = (row: JobListRow): void => {
    const segment = jobRowSegment(row.status, row.stageBar && shownStageBar(row.stageBar, hiddenStages));
    if (segment === null) void navigate({ to: '/jobs/$jobId', params: { jobId: row.id } });
    else void navigate({ to: '/jobs/$jobId/$tab', params: { jobId: row.id, tab: segment } });
  };

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div className={styles.heading}>
          <h1 className={styles.title}>{SCOPE_LABELS[scope]}</h1>
          {/* Counts are shown, not hinted, so nobody opens a screen to find out whether there is work in it. */}
          <span className={styles.total}>{total === undefined ? EMPTY : formatNumber(total)}</span>
        </div>
        <div className={styles.actions}>
          <Segmented<JobListScope>
            label="Whose jobs"
            value={scope}
            options={JOB_LIST_SCOPES.map((value) => ({ value, label: SCOPE_LABELS[value] }))}
            onChange={(next) => setSearch({ ...search, scope: next })}
          />
          {canCreate && isRequestsOn && <StartJobButton />}
          {canCreate && <NewJobButton isPrimary={!isRequestsOn} />}
        </div>
      </header>

      <section className={styles.card} aria-label="Jobs">
        <div className={styles.toolbar}>
          <span className={styles.search}>
            <SearchInput
              aria-label="Search jobs"
              value={query}
              onChange={(event) => setSearch({ ...search, q: event.target.value === '' ? undefined : event.target.value })}
              onClear={() => setSearch({ ...search, q: undefined })}
              placeholder="Search"
              // `/` focuses the primary search on any list screen (06-qol §2).
              data-primary-search
            />
          </span>
          {filters.length > 0 && (
            <>
              <span className={styles.filterLabel}>Filters</span>
              {filters.map((filter) => (
                <span key={filter.id} className={styles.chip}>
                  {filter.label}
                  <button type="button" aria-label={`Remove ${filter.label}`} onClick={filter.onRemove}>
                    <X size={13} aria-hidden />
                  </button>
                </span>
              ))}
              <button type="button" className={styles.clearAll} onClick={clearFilters}>
                Clear all
              </button>
            </>
          )}
          <span className={styles.grow} />
          <ViewMenu
            facets={facets.data}
            statuses={statuses}
            onStatuses={(next) => setSearch({ ...search, status: next.length > 0 ? next.join(',') : undefined })}
            ventureId={search.venture ?? null}
            ventureFallback={(ventures.data ?? []).map((v) => ({ value: v.id, label: v.name }))}
            onVenture={(value) => setSearch({ ...search, venture: value ?? undefined, department: undefined })}
            departmentId={search.department ?? null}
            departments={(departments.data ?? [])
              .filter((d) => search.venture === undefined || d.ventureId === search.venture)
              .map((d) => ({ value: d.id, label: d.name }))}
            onDepartment={(value) => setSearch({ ...search, department: value ?? undefined })}
          />
          <ColumnsMenuButton columns={columns} preferences={preferences} views={views} />
        </div>

        <DataTable
          screenId={SCREEN_ID}
          label="Jobs"
          columns={columns}
          rows={rows}
          preferences={preferences}
          isColumnMenuInHeader={false}
          toneOf={(row) => JOB_STATUS_TONES[row.status]}
          toneMark="dot"
          getRowId={(row) => row.id}
          total={total}
          sort={sort}
          onSortChange={setSort}
          selection={selection}
          onSelectionChange={setSelection}
          onOpenRow={openJob}
          linkOf={(row) => jobRowHref(row.id, jobRowSegment(row.status, row.stageBar && shownStageBar(row.stageBar, hiddenStages)))}
          exportName="jobs"
          views={views}
          isLoading={jobs.isPending}
          emptyState={
            filterCount > 0 ? (
              <FilteredEmptyState filterCount={filterCount} onClearFilters={clearFilters} isInset />
            ) : (
              <EmptyState
                title="No jobs yet"
                body={isRequestsOn ? 'Start a job from an approved hire request, or create one directly.' : 'Create the first job here.'}
                action={canCreate ? <NewJobButton isPrimary /> : undefined}
                isInset
              />
            )
          }
        />
        {rows.length > 0 && total !== undefined && (
          <Pager page={page} pageCount={pageCountOf(total, PAGE_SIZE)} pageSize={PAGE_SIZE} rowCount={rows.length} total={total} onPage={setPage} />
        )}
      </section>
    </div>
  );
}

/** The table's columns, in the mockup's order. */
function jobColumns(sections: readonly JobBuilderSection[], hiddenStages: ReadonlySet<StageBarKey>): readonly Column<JobListRow>[] {
  const segmentOf = (row: JobListRow): string | null => jobRowSegment(row.status, row.stageBar && shownStageBar(row.stageBar, hiddenStages));
  return [
    {
      id: 'reference',
      header: 'Reference',
      isIdentity: true,
      defaultWidth: 140,
      cell: (row) => (
        <JobLink jobId={row.id} segment={segmentOf(row)} className={styles.ref}>
          {row.reference}
        </JobLink>
      ),
      exportValue: (row) => row.reference,
    },
    {
      id: 'title',
      header: 'Job',
      defaultWidth: 250,
      cell: (row) => (
        <span className={styles.stack}>
          {/* The reference is the keyboard's way in; this second link is for the mouse. */}
          <JobLink jobId={row.id} segment={segmentOf(row)} className={styles.jobTitle} tabIndex={-1}>
            {row.publicTitle ?? row.title}
          </JobLink>
          {row.publicTitle && row.publicTitle !== row.title && <span className={styles.meta}>{row.title}</span>}
        </span>
      ),
      exportValue: (row) =>
        row.publicTitle && row.publicTitle !== row.title ? `${row.publicTitle} · ${row.title}` : row.publicTitle ?? row.title,
    },
    {
      id: 'stages',
      header: 'Stages',
      defaultWidth: 440,
      cell: (row) =>
        row.stageBar ? (
          <StageTrackCell jobId={row.id} title={row.publicTitle ?? row.title} status={row.status} bar={row.stageBar} hiddenStages={hiddenStages} />
        ) : null,
      exportValue: (row) =>
        row.stageBar
          ? shownStageBar(row.stageBar, hiddenStages)
              .stages.filter((stage) => stage.count !== null)
              .map((stage) => `${stage.label} ${stage.count}`)
              .join(' · ')
          : '',
    },
    {
      id: 'activity',
      header: 'Last activity',
      defaultWidth: 130,
      cell: (row) =>
        row.lastActivityAt ? (
          <time className={styles.figure} dateTime={row.lastActivityAt} title={formatDateTimeLong(row.lastActivityAt)}>
            {formatRelative(row.lastActivityAt)}
          </time>
        ) : null,
      exportValue: (row) => row.lastActivityAt ?? '',
    },
    {
      id: 'unit',
      header: 'Unit',
      defaultWidth: 190,
      cell: (row) => (
        <span className={styles.stack}>
          <span className={styles.strong}>{row.departmentName}</span>
          <span className={styles.meta}>{row.ventureName}</span>
        </span>
      ),
      exportValue: (row) => `${row.departmentName} · ${row.ventureName}`,
    },
    {
      id: 'status',
      header: 'Status',
      defaultWidth: 170,
      cell: (row) => (
        <span className={styles.chips}>
          <ToneChip tone={JOB_STATUS_TONES[row.status]}>{JOB_STATUS_LABELS[row.status]}</ToneChip>
          {row.isConfidential && (
            <Chip tone="attention" plain>
              Confidential
            </Chip>
          )}
        </span>
      ),
      exportValue: (row) => `${JOB_STATUS_LABELS[row.status]}${row.isConfidential ? ' · Confidential' : ''}`,
    },
    {
      id: 'setup',
      header: 'Setup',
      defaultWidth: 130,
      // R1: counted over the sections in use, so a switched-off one never reads as missing (§L34).
      cell: (row) => (
        <span className={styles.figure}>{`${row.completeSections.filter((section) => sections.includes(section)).length} of ${sections.length} sections`}</span>
      ),
      exportValue: (row) => `${row.completeSections.filter((section) => sections.includes(section)).length} of ${sections.length} sections`,
    },
    { id: 'recruiter', header: 'Recruiter', defaultWidth: 150, cell: (row) => person(row.recruiterName), exportValue: (row) => row.recruiterName },
    // JB-2 (R3): who asked for the hire; the hiring manager stays as its own column.
    { id: 'requestedBy', header: 'Requested by', defaultWidth: 150, cell: (row) => person(row.requestedByName), exportValue: (row) => row.requestedByName },
    { id: 'hiringManager', header: 'Hiring manager', defaultWidth: 150, cell: (row) => person(row.hiringManagerName), exportValue: (row) => row.hiringManagerName },
    { id: 'hires', header: 'Hires', isNumeric: true, defaultWidth: 80, cell: (row) => `${row.slotsFilled} of ${row.slotsTotal}` },
    { id: 'template', header: 'Template', defaultWidth: 150, cell: (row) => person(row.roleTemplateName), exportValue: (row) => row.roleTemplateName },
    {
      id: 'close',
      header: 'Target close',
      defaultWidth: 120,
      cell: (row) => (row.targetCloseDate ? <span className={styles.figure}>{formatIsoDate(row.targetCloseDate)}</span> : null),
      exportValue: (row) => (row.targetCloseDate ? formatIsoDate(row.targetCloseDate) : ''),
    },
    {
      id: 'created',
      header: 'Created',
      defaultWidth: 120,
      cell: (row) => <span className={styles.figure}>{formatDate(row.createdAt)}</span>,
      exportValue: (row) => formatDate(row.createdAt),
    },
  ];
}

/** A name in the mockup's weight; nobody is an em dash (06-qol §3), which the table draws for null. */
function person(name: string | null): ReactNode {
  return name === null || name === '' ? null : <span className={styles.name}>{name}</span>;
}

/** A row's link: a draft opens its setup, a live job the stage where people are now (`job-list-link.ts`). */
function JobLink({
  jobId,
  segment,
  className,
  tabIndex,
  children,
}: {
  readonly jobId: string;
  readonly segment: string | null;
  readonly className: string | undefined;
  readonly tabIndex?: number;
  readonly children: ReactNode;
}): ReactNode {
  if (segment === null) {
    return (
      <Link to="/jobs/$jobId" params={{ jobId }} className={className} tabIndex={tabIndex}>
        {children}
      </Link>
    );
  }
  return (
    <Link to="/jobs/$jobId/$tab" params={{ jobId, tab: segment }} className={className} tabIndex={tabIndex}>
      {children}
    </Link>
  );
}

interface FacetChoice {
  readonly value: string;
  readonly label: string;
  /** Absent until the counts first arrive. */
  readonly count?: number;
}

interface ViewMenuProps {
  readonly facets: JobListFacets | undefined;
  readonly statuses: readonly JobOpeningStatus[];
  readonly onStatuses: (next: readonly JobOpeningStatus[]) => void;
  readonly ventureId: string | null;
  /** The ventures by name, for the moment before the counts first arrive. */
  readonly ventureFallback: readonly FacetChoice[];
  readonly onVenture: (value: string | null) => void;
  readonly departmentId: string | null;
  readonly departments: readonly FacetChoice[];
  readonly onDepartment: (value: string | null) => void;
}

/** The mockup's View menu: density, then Status (ticks), Venture (one) and Department, each with its count. */
function ViewMenu({ facets, statuses, onStatuses, ventureId, ventureFallback, onVenture, departmentId, departments, onDepartment }: ViewMenuProps): ReactNode {
  const { isOpen, anchor, triggerRef, toggle, close } = usePopover();
  const density = useUi((state) => state.density);
  const setDensity = useUi((state) => state.setDensity);
  const ventureOptions: readonly FacetChoice[] = facets?.ventures ?? ventureFallback;

  const toggleStatus = (status: JobOpeningStatus): void =>
    onStatuses(statuses.includes(status) ? statuses.filter((item) => item !== status) : JOB_OPENING_STATUSES.filter((item) => item === status || statuses.includes(item)));

  return (
    <>
      <Button ref={triggerRef} variant="outline" size="sm" icon={SlidersHorizontal} onClick={toggle} aria-haspopup="menu" aria-expanded={isOpen}>
        View
      </Button>
      <Popover anchor={anchor} onClose={close} align="right" width={VIEW_MENU_WIDTH} label="View options">
        <MenuGroup label="Density">
          {DENSITIES.map((value) => (
            <MenuItem key={value} isSelected={density === value} onClick={() => setDensity(value)} meta={density === value ? <Check size={14} aria-hidden /> : undefined}>
              {DENSITY_LABELS[value]}
            </MenuItem>
          ))}
        </MenuGroup>
        <MenuSeparator />
        <MenuGroup label="Status">
          {JOB_OPENING_STATUSES.map((status) => (
            <StatusOption
              key={status}
              status={status}
              count={facets?.statuses.find((option) => option.value === status)?.count}
              isOn={statuses.includes(status)}
              onToggle={() => toggleStatus(status)}
            />
          ))}
        </MenuGroup>
        <MenuSeparator />
        <MenuGroup label="Venture">
          {ventureOptions.map((option) => {
            const isOn = option.value === ventureId;
            return (
              <MenuItem
                key={option.value}
                isSelected={isOn}
                onClick={() => onVenture(isOn ? null : option.value)}
                meta={
                  <span className={styles.optionMeta}>
                    {option.count !== undefined && <span className={styles.count}>{formatNumber(option.count)}</span>}
                    {isOn && <Check size={14} aria-hidden />}
                  </span>
                }
              >
                {option.label}
              </MenuItem>
            );
          })}
        </MenuGroup>
        <MenuSeparator />
        <div className={styles.department}>
          <Field label="Department">
            {({ inputId }) => (
              <Select
                id={inputId}
                label="Department"
                value={departmentId ?? ''}
                options={[{ value: '', label: 'Every department' }, ...departments]}
                onChange={(value) => onDepartment(value === '' ? null : value)}
              />
            )}
          </Field>
        </div>
      </Popover>
    </>
  );
}

/** A status with its tick box, its colour and how many jobs it holds. The whole row toggles. */
function StatusOption({ status, count, isOn, onToggle }: { readonly status: JobOpeningStatus; readonly count: number | undefined; readonly isOn: boolean; readonly onToggle: () => void }): ReactNode {
  const label = JOB_STATUS_LABELS[status];
  return (
    <div className={styles.option} onClick={onToggle} role="presentation">
      <span className={styles.box} onClick={(event) => event.stopPropagation()} role="presentation">
        <Checkbox checked={isOn} onChange={onToggle} ariaLabel={count === undefined ? label : `${label}, ${formatNumber(count)}`} />
      </span>
      <span className={`${toneClass} ${styles.dot}`} data-tone={JOB_STATUS_TONES[status]} aria-hidden />
      <span className={styles.optionLabel}>{label}</span>
      {count !== undefined && <span className={styles.count}>{formatNumber(count)}</span>}
    </div>
  );
}

/** F5.24 (R1): a job created directly, with no hire request behind it. */
function NewJobButton({ isPrimary }: { readonly isPrimary: boolean }): ReactNode {
  const navigate = useNavigate();
  return (
    <Button variant={isPrimary ? 'solid' : 'outline'} icon={FilePlus2} onClick={() => void navigate({ to: '/jobs/new' })}>
      New job
    </Button>
  );
}

/** The approved requisitions waiting for a job, one click from starting each. */
function StartJobButton(): ReactNode {
  const { isOpen, anchor, triggerRef, toggle, close } = usePopover();
  const ready = useReadyRequisitions(isOpen);
  const navigate = useNavigate();

  return (
    <>
      <Button ref={triggerRef} variant="solid" icon={Plus} onClick={toggle} aria-expanded={isOpen} aria-haspopup="menu">
        Start a job
      </Button>
      <Popover anchor={anchor} onClose={close} align="right" width={START_MENU_WIDTH} label="Approved requisitions without a job">
        {ready.isPending && <MenuItem disabled>Loading approved requisitions…</MenuItem>}
        {ready.data?.length === 0 && <MenuItem disabled>Every approved requisition already has its job</MenuItem>}
        {(ready.data ?? []).map((requisition) => (
          <MenuItem
            key={requisition.id}
            meta={`${requisition.reference} · ${requisition.departmentName}`}
            onClick={() => {
              close();
              void navigate({ to: '/jobs/new/$requisitionId', params: { requisitionId: requisition.id } });
            }}
          >
            {requisition.title}
          </MenuItem>
        ))}
      </Popover>
    </>
  );
}
